import { getSites, INameID } from '~/src/dash/api/console-api';
import {
  fetchDealTargetedChannelGroups,
  fetchDealTargetedChannels,
} from '~/src/dash/apiV2/channels';

type simplifiedChannelsPreload =
  | 'ChannelLabels'
  | 'SubSites'
  | 'ChannelPerformanceSortByOptions'
  | 'SitesCronSummary';

export const simplifiedChannelsPreloads = {
  CHANNEL_LABELS: 'ChannelLabels' as simplifiedChannelsPreload,
  CHANNEL_PERFORMANCE_SORT_BY_OPTIONS:
    'ChannelPerformanceSortByOptions' as simplifiedChannelsPreload,
  SUB_SITES: 'SubSites' as simplifiedChannelsPreload,
  SITES_CRON_SUMMARY: 'SitesCronSummary' as simplifiedChannelsPreload,
};

export type simplifiedChannelsKey =
  | 'created_at'
  | 'updated_at'
  | 'deleted_at'
  | 'id'
  | 'uuid'
  | 'name'
  | 'active'
  | 'bid_floor'
  | 'header_bidder_dedup'
  | 'header_bidder_dedup_adomain'
  | 'header_bidder_dedup_aps_key'
  | 'header_bidder_dedup_category'
  | 'is_marketplace_connection'
  | 'subscription_type';

// use these to prevent misspellings and nonexistent options
export const simplifiedChannelsKeys = {
  CREATED_AT: 'created_at' as simplifiedChannelsKey,
  UPDATED_AT: 'updated_at' as simplifiedChannelsKey,
  DELETED_AT: 'deleted_at' as simplifiedChannelsKey,
  ID: 'id' as simplifiedChannelsKey,
  UUID: 'uuid' as simplifiedChannelsKey,
  NAME: 'name' as simplifiedChannelsKey,
  ACTIVE: 'active' as simplifiedChannelsKey,
  BIDFLOOR: 'bid_floor' as simplifiedChannelsKey,
  HEADER_BIDDER_DEDUP: 'header_bidder_dedup' as simplifiedChannelsKey,
  HEADER_BIDDER_DEDUP_ADOMAIN: 'header_bidder_dedup_adomain' as simplifiedChannelsKey,
  HEADER_BIDDER_DEDUP_APS_KEY: 'header_bidder_dedup_aps_key' as simplifiedChannelsKey,
  HEADER_BIDDER_DEDUP_CATEGORY: 'header_bidder_dedup_category' as simplifiedChannelsKey,
  IS_MARKETPLACE_CONNECTION: 'is_marketplace_connection' as simplifiedChannelsKey,
  SUBSCRIPTION_TYPE: 'subscription_type' as simplifiedChannelsKey,
};

type orderDirection = 'asc' | 'desc';

export type activeChannelsFilter = '0' | '1' | '2';

export interface IGetSitesParams {
  publisherIDs: string[];
  publisherID: string;
  orderBy?: simplifiedChannelsKey | '';
  orderDirection?: orderDirection | '';
  nameSearch?: string;
  idSearch?: number | '';
  activeFilter?: activeChannelsFilter;
  page?: number | string;
  pageSize?: number | string;
  preloads?: simplifiedChannelsPreload[];
  uiFields?: simplifiedChannelsKey[];
  siteIDs?: number[];
}

export interface IGetDealTargetedSitesParams {
  publisherID: string;
  dealID: number;
}
// utilizes SimplifiedSiteInfo struct
export const getSitesPromise = ({
  publisherIDs,
  publisherID,
  siteIDs = [],
  orderBy = '',
  orderDirection = '',
  nameSearch = '',
  idSearch = '',
  activeFilter = '0',
  page = 0,
  pageSize = 0,
  preloads = [], // default sends an empty array; no preloads
  uiFields = [], // default sends an empty array; returns all direct struct key-value pairs
}: IGetSitesParams) =>
  getSites({
    query: {
      publisher_ids: publisherIDs.join(','),
      publisher_id: publisherID,
      site_ids: siteIDs.join(','),
      // ordering
      order_by: orderBy,
      order_direction: orderDirection,
      // searching
      name_search: nameSearch,
      id_search: idSearch,
      // filtering
      active_filter: activeFilter,
      page: page.toString(),
      page_size: pageSize.toString(),
      // customization
      preloads: preloads.join(','),
      ui_fields: uiFields.join(','),
    },
  });

export const getDealTargetedSitesPromise = ({
  publisherID,
  dealID,
}: IGetDealTargetedSitesParams): Promise<INameID[]> =>
  fetchDealTargetedChannels(publisherID, dealID);

export const getDealTargetedChannelGroupsPromise = ({
  publisherID,
  dealID,
}: IGetDealTargetedSitesParams): Promise<INameID[]> =>
  fetchDealTargetedChannelGroups(publisherID, dealID);
